import React, { useEffect } from 'react';
import { Button, Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { theme } from 'theme';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { handleTag } from 'store/intercomSlice';
import { HeaderPage } from 'components/HeaderPage/HeaderPage';
import { DASHBOARD_ROUTES } from 'Routes';
import { UserInfo } from '@data/models/user.model';
import { selectUser } from 'store/userSlide';
import { selectNavigateLinkHeader } from 'store/navigateSlide';
import { selectCompany } from 'store/companySlide';
import { CompanyInfo } from '@data/models/company.model';
import { ConversationThread } from 'components/conversation/ConversationThread';
import { DraftReplyPanel } from 'components/conversation/DraftReplyPanel';
import {
  useAiDraftEnabled,
  useDraftReply,
} from 'components/conversation/useDraftReply';

export const TabTexts: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user_id } = useParams();
  const conversation = useAppSelector((state) => state.intercom.converstation);
  const loading = useAppSelector((state) => state.intercom.loading);
  const userInfo: UserInfo = useSelector(selectUser);
  const navigateLinkHeader = useSelector(selectNavigateLinkHeader);
  const companyInfo: CompanyInfo = useSelector(selectCompany);
  const reply = useDraftReply(user_id);
  const aiDraftEnabled = useAiDraftEnabled();

  // The member list, user search and company-employees pages preselect a tag
  // before navigating here; arriving from anywhere else starts clean.
  // ROUTE_USER is user search (UserSearch.tsx) — it was missing from this
  // list on dev, so tags clicked there were cleared on arrival and never
  // jumped to their message.
  useEffect(() => {
    const tagOrigins = [
      DASHBOARD_ROUTES.COMPANY_ID_EMPLOYEES(companyInfo.companyId),
      DASHBOARD_ROUTES.ROUTE_USER_LIST,
      DASHBOARD_ROUTES.ROUTE_USER,
    ];
    if (!tagOrigins.includes(navigateLinkHeader.fromLink)) {
      dispatch(handleTag(''));
    }
    const clearOnUnload = () => dispatch(handleTag(''));
    window.addEventListener('beforeunload', clearOnUnload);
    return () => window.removeEventListener('beforeunload', clearOnUnload);
  }, []);

  const hasParts = !!conversation?.conversationPart?.length;

  return (
    <>
      <HeaderPage
        title={{
          title:
            userInfo?.firstName || userInfo?.lastName
              ? userInfo?.firstName + ' ' + userInfo?.lastName
              : '',
          hasBack: true,
        }}
        tabs={[
          {
            title: 'Overview',
            selected: false,
            link: DASHBOARD_ROUTES.USER_DETAIL_ID(userInfo.userId),
          },
          {
            title: 'Usage Logs',
            selected: false,
            link: DASHBOARD_ROUTES.USER_TRACKING(userInfo.userId),
          },
          {
            title: 'User Notes',
            selected: false,
            link: DASHBOARD_ROUTES.USER_ID_NOTES(userInfo.userId),
          },
          {
            title: 'User Events',
            selected: false,
            link: DASHBOARD_ROUTES.USER_ID_LOSS_EVENTS(userInfo.userId),
          },
          {
            title: 'Check-ins',
            selected: false,
            link: DASHBOARD_ROUTES.USER_ID_CHECK_INS(userInfo.userId),
          },
          {
            title: 'Conversations',
            selected: true,
            link: DASHBOARD_ROUTES.USER_ID_CONVERSATION(userInfo.userId),
          },
        ]}
      />
      <Stack
        sx={{
          [theme.breakpoints.down('md')]: { width: '100%', marginLeft: 0 },
          [theme.breakpoints.up(768)]: { width: '70%', marginLeft: '120px' },
          // A real height instead of the measured/absolute positioning this
          // page used to compute; the thread scrolls inside it.
          height: 'calc(100vh - 220px)',
          minHeight: '320px',
          marginTop: '24px',
        }}
      >
        <ConversationThread userId={user_id} />
      </Stack>

      {aiDraftEnabled && !reply.isOpen && !loading && hasParts && (
        <Button
          variant="contained"
          onClick={reply.requestDraft}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
            backgroundColor: '#356766',
            borderRadius: '20px',
            paddingX: '20px',
            textTransform: 'none',
            fontWeight: 600,
            ':hover': { backgroundColor: '#213B54' },
          }}
        >
          Draft reply with AI
        </Button>
      )}
      {reply.isOpen && <DraftReplyPanel reply={reply} />}
    </>
  );
};
